/// <amd-module name="Wit/WorkItemForm/Links/GitHub/GitHubArtifactDropdownWithInfiniteScrolling" />
import { useDebouncedSubscription, useObservable } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { useService } from "VSS/Platform/Layout";
import { Dropdown, DropdownCallout } from "VSSUI/Dropdown";
import { IIconProps } from "VSSUI/Icon";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import * as Resources from "Wit/WorkItemForm/Resources";
import * as React from "react";
import { GitHubPaginatedRequest, GitHubPaginatedResponse, GitHubRateLimit } from "./GitHubDataProviderContracts";
import { GitHubDropdownLoadingCell } from "./GitHubDropdownLoadingCell";
import { GitHubDropdownMessages } from "./GitHubDropdownMessages";

const pageSize = 100;

export enum IconType {
    SUCCESS,
    WARNING,
    ERROR,
    INFO,
    NONE
}

type IconColors<T> = {
    artifactAttribute: { [K in keyof T]-?: T[K] extends string ? K : never }[keyof T];
    artifactAttributeColors: {
        artifactAttributeValue: string;
        iconType: IconType;
        title: string;
    }[];
};

interface IGitHubArtifactDropdownWithInfiniteScrollingProps<T> {
    pageContext: IVssPageContext;
    selectedArtifact: T | undefined;
    setSelectedArtifact: (artifact: T | undefined) => void;
    dropdownIconName?: string;
    dropdownIconColors?: IconColors<T>;
    dropdownIconRender?: () => JSX.Element;
    dropdownId: keyof T;
    dropdownText: Array<keyof T>;
    dropdownSortSelectedItemByText?: boolean;
    infiniteScrollingDataProviderId: string;
    cacheArtifacts?: boolean;
    setShowSetUrlManually: (value: boolean) => void;
    resources: {
        dropdownTextTemplate?: string;
        noResultsMessage: string;
        loadingMessage: string;
        loadingFailedMessage: string;
        entities: string;
    };
    requireGitHubRepositoryContext?: boolean;
    gitHubRepositoryContext?: ExternalGitRepo;
}

const getDropdownItemId = <T,>(artifact: T, idAttribute: keyof T): string => String(artifact[idAttribute]);

const getDropdownItemText = <T,>(artifact: T, textAttributes: Array<keyof T>, dropdownTextTemplate?: string): string => {
    const stringTextAttributes = textAttributes.map(textAttribute => String(artifact[textAttribute]));
    return dropdownTextTemplate
        ? format(dropdownTextTemplate, ...stringTextAttributes)
        : textAttributes.map(textAttribute => String(artifact[textAttribute])).join(" ");
};

const getIconProperties = <T,>(artifact: T, iconColors: IconColors<T>): { className: string; title: string | undefined } => {
    const artifactAttributeValue = String(artifact[iconColors.artifactAttribute]);
    const baseClass = "github-artifact-icon";
    for (let i = 0; i < iconColors.artifactAttributeColors.length; ++i) {
        const iconColor = iconColors.artifactAttributeColors[i];
        if (localeIgnoreCaseComparer(artifactAttributeValue, iconColor.artifactAttributeValue) === 0) {
            let className = "";
            switch (iconColor.iconType) {
                case IconType.SUCCESS:
                    className = `${baseClass}-success`;
                    break;
                case IconType.WARNING:
                    className = `${baseClass}-warning`;
                    break;
                case IconType.ERROR:
                    className = `${baseClass}-error`;
                    break;
                case IconType.INFO:
                    className = `${baseClass}-info`;
                    break;
                case IconType.NONE:
                default:
                    break;
            }
            return { className, title: iconColor.title };
        }
    }
    return { className: "", title: undefined };
};

const getDropdownItem = <T,>(
    artifact: T,
    icon: string | (() => JSX.Element),
    iconColors: IconColors<T> | undefined,
    idAttribute: keyof T,
    textAttributes: Array<keyof T>,
    dropdownTextTemplate?: string
): IListBoxItem<T> => {
    let iconProps: IIconProps;
    if (typeof icon === "string") {
        iconProps = {
            iconName: icon,
            ...(iconColors ? getIconProperties(artifact, iconColors) : {})
        };
    } else {
        iconProps = { render: icon };
    }
    return {
        id: getDropdownItemId(artifact, idAttribute),
        text: getDropdownItemText(artifact, textAttributes, dropdownTextTemplate),
        data: { ...artifact },
        iconProps
    };
};

const errorRowId = "error";
const loadingRowId = "loading";
const unknownError = "Something went wrong"; // this error is not shown to the user, so does not need to be in the Resources
const maximumFilterLength = 256;

export const GitHubArtifactDropdownWithInfiniteScrolling = <Artifact,>(props: IGitHubArtifactDropdownWithInfiniteScrollingProps<Artifact>) => {
    const contributionService = useService<IVssContributionService>("IVssContributionService");
    const [loading, setLoading] = useObservable<boolean>(false);
    const [error, setError] = React.useState<string>();
    const [filterText, setFilterText] = useObservable<string>("");
    const [artifacts, setArtifacts] = React.useState<Artifact[]>();
    const [rateLimitInformation, setRateLimitInformation] = React.useState<GitHubRateLimit>();
    const [hasReachedEndOfArtifactsList, setHasReachedEndOfArtifactsList] = React.useState<boolean>(false);
    const [continuationToken, setContinuationToken] = React.useState<string>();
    const artifactSelection = React.useRef(new DropdownSelection());

    const loadArtifacts = React.useCallback(
        async (newFilterText?: string) => {
            if (props.requireGitHubRepositoryContext && !props.gitHubRepositoryContext) {
                return;
            }
            const setupErrorState = (error: string) => {
                setError(error);
                setLoading(false);
                setHasReachedEndOfArtifactsList(true);
            };
            try {
                const hasFilterChanged = newFilterText !== undefined;
                if (!hasFilterChanged && loading.value) {
                    // loading of the previous page is still in progress - skip this request
                    return;
                }
                setLoading(true);
                if (hasFilterChanged) {
                    setHasReachedEndOfArtifactsList(false);
                    setArtifacts(undefined);
                    setContinuationToken(undefined);
                }

                const parameters: GitHubPaginatedRequest = {
                    pageSize,
                    continuationToken: hasFilterChanged ? undefined : continuationToken,
                    filter: hasFilterChanged ? newFilterText : filterText.value,
                    repositoryName: props.gitHubRepositoryContext ? props.gitHubRepositoryContext.name : undefined,
                    repositoryExternalId: props.gitHubRepositoryContext ? props.gitHubRepositoryContext.id : undefined
                };
                const newArtifacts = await contributionService.getDataAsync<GitHubPaginatedResponse<Artifact>>(
                    props.infiniteScrollingDataProviderId,
                    parameters,
                    !props.cacheArtifacts
                );

                if (filterText.value !== parameters.filter) {
                    // the filter has changed after the request to BE was fired => skip it
                    return;
                }

                if (!newArtifacts) {
                    const exception = contributionService.getDataProviderExceptions()[props.infiniteScrollingDataProviderId];
                    setupErrorState(exception.message ?? unknownError);
                } else if ((newArtifacts as any)?.errorMessage) {
                    setupErrorState((newArtifacts as any).errorMessage);
                } else {
                    if (!newArtifacts.data) {
                        setupErrorState(unknownError);
                        return;
                    }
                    setError(undefined);
                    setLoading(false);
                    setContinuationToken(newArtifacts.continuationToken);
                    setArtifacts(previousArtifacts =>
                        previousArtifacts === undefined ? newArtifacts.data : [...previousArtifacts, ...newArtifacts.data]
                    );
                    if (!newArtifacts.continuationToken) {
                        setHasReachedEndOfArtifactsList(true);
                    }
                    if (newArtifacts.rateLimit) {
                        setRateLimitInformation(newArtifacts.rateLimit);
                    }
                }
            } catch (e) {
                setupErrorState(unknownError);
            }
        },
        [
            props.pageContext,
            props.infiniteScrollingDataProviderId,
            props.requireGitHubRepositoryContext,
            props.gitHubRepositoryContext,
            props.cacheArtifacts,
            contributionService,
            setLoading,
            setError,
            setArtifacts,
            setContinuationToken,
            setHasReachedEndOfArtifactsList,
            continuationToken
        ]
    );

    React.useEffect(() => {
        loadArtifacts(filterText.value);
    }, [props.gitHubRepositoryContext]);

    useDebouncedSubscription(filterText, 300, loadArtifacts, [loadArtifacts]);

    const selectedArtifact = props.selectedArtifact;
    const dropdownItems = React.useMemo(() => {
        const result: IListBoxItem<Artifact>[] = artifacts
            ? artifacts.map(artifact =>
                  getDropdownItem(
                      artifact,
                      props.dropdownIconRender ?? props.dropdownIconName ?? "",
                      props.dropdownIconColors,
                      props.dropdownId,
                      props.dropdownText,
                      props.resources.dropdownTextTemplate
                  )
              )
            : [];
        if (selectedArtifact) {
            const existingSelectedArtifact = result.find(
                exitingArtifact => exitingArtifact.id === getDropdownItemId(selectedArtifact, props.dropdownId)
            );
            if (!existingSelectedArtifact) {
                if (hasReachedEndOfArtifactsList) {
                    props.setSelectedArtifact(undefined);
                } else {
                    result.push(
                        getDropdownItem(
                            selectedArtifact,
                            props.dropdownIconRender ?? props.dropdownIconName ?? "",
                            props.dropdownIconColors,
                            props.dropdownId,
                            props.dropdownText,
                            props.resources.dropdownTextTemplate
                        )
                    );
                    if (props.dropdownSortSelectedItemByText) {
                        result.sort((a, b) =>
                            localeIgnoreCaseComparer(
                                a.data ? getDropdownItemText(a.data, props.dropdownText, props.resources.dropdownTextTemplate) : "",
                                b.data ? getDropdownItemText(b.data, props.dropdownText, props.resources.dropdownTextTemplate) : ""
                            )
                        );
                    }
                }
            }
        }
        if (!hasReachedEndOfArtifactsList) {
            result.push({
                id: loadingRowId,
                type: ListBoxItemType.Loading,
                render: (_, _2, _3, tableItem) => {
                    return (
                        <GitHubDropdownLoadingCell
                            key={tableItem.id}
                            pageContext={props.pageContext}
                            loadingLabel={props.resources.loadingMessage}
                            onShow={() => {
                                loadArtifacts();
                            }}
                        />
                    );
                }
            });
        }

        return result;
    }, [
        artifacts,
        hasReachedEndOfArtifactsList,
        selectedArtifact,
        error,
        loadArtifacts,
        setError,
        setHasReachedEndOfArtifactsList,
        props.setSelectedArtifact,
        props.dropdownIconRender,
        props.dropdownIconName,
        props.dropdownIconColors,
        props.dropdownId,
        props.dropdownText,
        props.dropdownSortSelectedItemByText,
        props.resources.dropdownTextTemplate,
        props.resources.loadingFailedMessage,
        props.resources.loadingMessage
    ]);

    React.useEffect(() => {
        const unselectableIndex = dropdownItems.findIndex(row => row.id === loadingRowId || row.id === errorRowId);
        artifactSelection.current.unselectableRanges = [{ beginIndex: unselectableIndex, endIndex: unselectableIndex }];

        if (selectedArtifact) {
            const index = dropdownItems.findIndex(artifact => artifact.id === getDropdownItemId(selectedArtifact, props.dropdownId));
            if (index !== -1) {
                artifactSelection.current.select(index);
            }
        } else {
            artifactSelection.current.clear();
        }
    }, [dropdownItems, selectedArtifact, loading, error, props.dropdownId]);

    const isRepositoryContextMissing = props.requireGitHubRepositoryContext && !props.gitHubRepositoryContext;
    return (
        <Observer loading={loading}>
            {({ loading }) => (
                <Dropdown<Artifact>
                    items={dropdownItems}
                    disabled={isRepositoryContextMissing}
                    placeholder={isRepositoryContextMissing ? Resources.GithubRepositoryContextMissing : undefined}
                    showFilterBox={true}
                    filterByText={false}
                    loading={loading}
                    onSelect={(_, item) => {
                        if (item.id === errorRowId || item.id === loadingRowId) {
                            return;
                        }
                        props.setSelectedArtifact(item.data);
                    }}
                    selection={artifactSelection.current}
                    filteredNoResultsText={props.resources.noResultsMessage}
                    renderCallout={calloutProps => (
                        <DropdownCallout
                            {...calloutProps}
                            listBoxClassName="github-artifact-listbox"
                            onFilterTextChanged={(e, value) => {
                                if (value.length <= maximumFilterLength && value !== filterText.value) {
                                    calloutProps.onFilterTextChanged && calloutProps.onFilterTextChanged(e, value);
                                    setFilterText(value);
                                }
                            }}
                        />
                    )}
                    renderBeforeContent={() => (
                        <GitHubDropdownMessages
                            nothingFoundInOurSystems={
                                !loading && hasReachedEndOfArtifactsList && !error && filterText.value === "" && dropdownItems.length === 0
                            }
                            error={error}
                            rateLimitInformation={rateLimitInformation}
                            resources={{
                                loadingFailedMessage: props.resources.loadingFailedMessage,
                                entities: props.resources.entities
                            }}
                            setShowSetUrlManually={props.setShowSetUrlManually}
                            setError={setError}
                            setHasReachedEndOfArtifactsList={setHasReachedEndOfArtifactsList}
                            loadArtifacts={loadArtifacts}
                            unknownError={unknownError}
                        />
                    )}
                />
            )}
        </Observer>
    );
};
